(() => {
  if (window.tuantyCartControlsReady) return;
  window.tuantyCartControlsReady = true;
  const root = () => window.Shopify?.routes?.root || '/';
  const controls = () => [...document.querySelectorAll('tuanty-cart-discount')];
  const notices = new Map();
  const drafts = new Map();
  const expanded = new Map();
  const pending = new Map();
  let lastCart;
  let hydration;
  let version = 0;
  let mutation = false;
  let nativeBusy = false;
  let uncertain = false;
  let timer;
  let nativeWatchdog;
  let nativeSubmitIntent;
  let nativeForm;
  let focusAfter;
  let gesture;
  let suppressClickUntil = 0;
  const normalize = (code) => String(code || '').trim().toLocaleUpperCase('en-US');
  const rowFor = (target) => target?.closest?.('[data-cart-swipe]') || target?.closest?.('.hdt-cart-item')?.querySelector('[data-cart-swipe]');
  const itemFor = (row) => row?.closest('.hdt-cart-item') || row;
  const quantityFor = (row) => itemFor(row)?.querySelector('[name="updates[]"]');
  const busy = () => mutation || nativeBusy || pending.size > 0 || uncertain;

  function identity(item) {
    const variant = item?.variant_id || item?.id;
    if (!variant) return null;
    const properties = Object.entries(item.properties || {}).sort(([left], [right]) => left.localeCompare(right));
    return JSON.stringify([String(variant), item.selling_plan_allocation?.selling_plan?.id || null, properties]);
  }

  function resolveItem(cart, key, signature) {
    const exact = cart?.items?.find((item) => item.key === key);
    if (exact) return exact;
    if (!signature) return null;
    const matches = (cart?.items || []).filter((item) => identity(item) === signature);
    return matches.length === 1 ? matches[0] : null;
  }

  function nativeTimeout() {
    if (!nativeBusy) return;
    nativeBusy = false;
    uncertain = true;
    pending.clear();
    clearTimeout(timer);
    announce('uncertain', 'error');
    syncBusy();
  }

  function codesFrom(cart) {
    const codes = new Map();
    for (const entry of cart?.discount_codes || []) {
      if (entry?.code) codes.set(normalize(entry.code), { code: entry.code, applicable: entry.applicable === true });
    }
    const applications = [...(cart?.cart_level_discount_applications || []), ...(cart?.discount_applications || [])];
    for (const item of cart?.items || []) {
      for (const allocation of item.line_level_discount_allocations || []) {
        if (Number(allocation.amount) > 0) applications.push({ ...allocation.discount_application, total_allocated_amount: allocation.amount });
      }
    }
    for (const application of applications) {
      if (application?.type === 'discount_code' && application.title && Number(application.total_allocated_amount) > 0) {
        codes.set(normalize(application.title), { code: application.title, applicable: true });
      }
    }
    return [...codes.values()];
  }

  async function json(url, options = {}) {
    const response = await fetch(url, {
      credentials: 'same-origin', cache: 'no-store', signal: AbortSignal.timeout(15000), ...options,
      headers: { Accept: 'application/json', ...options.headers }
    });
    const data = await response.json();
    if (!response.ok || !data || data.status >= 400) throw new Error('cart_request_failed');
    return data;
  }

  function sectionIds() {
    const sections = controls().map((element) => element.dataset.sectionId).filter(Boolean);
    document.dispatchEvent(new CustomEvent('cart:push:section.id', { detail: { sections } }));
    return [...new Set(sections)].slice(0, 5);
  }

  async function renderCart(cart, sections) {
    if (!Array.isArray(cart?.items)) throw new Error('cart_response_invalid');
    if (sections.some((id) => !cart.sections?.[id])) {
      const url = new URL(window.location.href);
      url.searchParams.set('sections', sections.join(','));
      cart.sections = await json(url.toString(), { headers: { Accept: '*/*' } });
    }
    if (sections.some((id) => !cart.sections?.[id])) throw new Error('cart_render_failed');
    lastCart = cart;
    document.dispatchEvent(new CustomEvent('cart:change', {
      bubbles: true, detail: { baseEvent: 'line-item:change', cart, tuantyControls: true }
    }));
  }

  function announce(key, tone = 'success', scope) {
    const targets = scope ? controls().filter((element) => element.dataset.sectionId === scope) : controls();
    for (const element of targets) {
      const notice = { key, tone };
      notices.set(element.dataset.sectionId, notice);
      element.showNotice(notice);
    }
  }

  function syncBusy() {
    for (const element of controls()) element.setBusy(busy());
    document.querySelectorAll('[data-cart-swipe]').forEach((row) => {
      const item = itemFor(row);
      item.setAttribute('aria-busy', String(mutation || nativeBusy));
      item.inert = mutation || nativeBusy || uncertain;
    });
    document.querySelectorAll('[data-tuanty-add], .hdt-mini-cart__view-checkout, .hdt-page-cart__footer [name="checkout"]').forEach((element) => {
      element.inert = busy();
    });
  }

  function restoreFocus() {
    syncBusy();
    if (!focusAfter || mutation || nativeBusy) return;
    if (!lastCart?.item_count && document.querySelector('[data-cart-swipe]')) return;
    const intended = focusAfter;
    focusAfter = null;
    requestAnimationFrame(() => {
      let target;
      if (intended.discount) {
        const element = controls().find((node) => node.dataset.sectionId === intended.section);
        target = intended.removeCode ? element?.querySelector('[data-discount-remove]') : null;
        target ||= element?.details && !element.details.open ? element.disclosure : element?.querySelector('[data-discount-input]');
      } else {
        if (intended.button === 'pointer') return;
        const scope = document.getElementById(intended.scope) || document;
        const rows = [...scope.querySelectorAll('[data-cart-swipe]')];
        const row = rows.find((node) => node.dataset.cartKey === intended.key) || rows[Math.min(intended.index, rows.length - 1)];
        target = intended.remove ? itemFor(row)?.querySelector('.hdt-mini-cart__remove, .hdt-page-cart__remove') :
          intended.button ? itemFor(row)?.querySelector(`.hdt-quantity__button[name="${intended.button}"]`) : quantityFor(row);
        target ||= scope.querySelector('[name="checkout"], .hdt-mini-cart__emty-button, .hdt-page-cart__emty-button');
      }
      target?.focus({ preventScroll: true });
    });
  }

  async function changeCart(payload, sectionList) {
    return json(`${root()}cart/${payload.discount !== undefined ? 'update' : 'change'}.js`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ ...payload, sections: sectionList, sections_url: window.location.pathname })
    });
  }

  async function recover(sectionList, verify, successKey, errorKey) {
    try {
      const cart = await json(`${root()}cart.js`);
      await renderCart(cart, sectionList);
      announce(verify(cart) ? successKey : errorKey, verify(cart) ? 'success' : 'error');
    } catch {
      uncertain = true;
      announce('uncertain', 'error');
    }
  }

  function start() {
    mutation = true;
    version += 1;
    syncBusy();
    document.dispatchEvent(new CustomEvent('theme:loading:doing', { detail: { tuantyControls: true } }));
  }

  function finish() {
    mutation = false;
    document.dispatchEvent(new CustomEvent('theme:loading:done', { detail: { tuantyControls: true } }));
    restoreFocus();
    if (pending.size && !uncertain && !nativeBusy) timer = setTimeout(flushQuantity, 0);
  }

  async function flushQuantity() {
    if (mutation || nativeBusy || uncertain || !pending.size) return;
    const [originalKey, intent] = pending.entries().next().value;
    pending.delete(originalKey);
    const source = lastCart || controls()[0]?.cart;
    const currentItem = resolveItem(source, originalKey, intent.identity);
    const key = currentItem?.key || originalKey;
    const totalForIdentity = (cart) => (cart?.items || []).filter((item) => identity(item) === intent.identity)
      .reduce((total, item) => total + item.quantity, 0);
    const expectedTotal = intent.identity && currentItem ? totalForIdentity(source) - currentItem.quantity + intent.quantity : null;
    const sectionList = sectionIds();
    focusAfter = intent.focus;
    start();
    announce('updating');
    const successKey = intent.quantity === 0 ? 'removed_item' : 'quantity_updated';
    const verify = (cart) => {
      if (expectedTotal !== null) return totalForIdentity(cart) === expectedTotal;
      if (intent.quantity === 0) return !cart.items.some((item) => item.key === key || (intent.identity && identity(item) === intent.identity));
      const found = resolveItem(cart, key, intent.identity);
      return found?.quantity === intent.quantity;
    };
    try {
      if (source && !currentItem) throw new Error('cart_item_changed');
      const cart = await changeCart({ id: key, quantity: intent.quantity }, sectionList);
      await renderCart(cart, sectionList);
      announce(verify(cart) ? successKey : 'quantity_error', verify(cart) ? 'success' : 'error');
    } catch {
      await recover(sectionList, verify, successKey, 'quantity_error');
    } finally { finish(); }
  }

  function queueQuantity(row, quantity, remove = false, button = null) {
    if (!row || mutation || nativeBusy || uncertain) return;
    const scope = row.closest('hdt-cart-drawer, #hdt-page-cart');
    const sourceItem = (lastCart || controls()[0]?.cart)?.items?.find((item) => item.key === row.dataset.cartKey);
    pending.set(row.dataset.cartKey, {
      quantity,
      identity: identity(sourceItem),
      focus: { key: row.dataset.cartKey, index: [...scope.querySelectorAll('[data-cart-swipe]')].indexOf(row), scope: scope.id, remove, button }
    });
    closeSwipe(row);
    clearTimeout(timer);
    syncBusy();
    timer = setTimeout(flushQuantity, remove ? 0 : 300);
  }

  class TuantyCartDiscount extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      try {
        const decoder = document.createElement('textarea');
        this.strings = Object.fromEntries(Object.entries(JSON.parse(this.querySelector('[data-discount-strings]').textContent)).map(([key, value]) => {
          decoder.innerHTML = String(value);
          return [key, decoder.value];
        }));
        this.cart = JSON.parse(this.querySelector('[data-discount-cart]').textContent);
      } catch { this.hidden = true; return; }
      this.input = this.querySelector('[data-discount-input]');
      this.applyButton = this.querySelector('[data-discount-apply]');
      this.details = this.querySelector('[data-discount-details]');
      this.disclosure = this.querySelector('[data-discount-toggle]');
      this.setExpanded(expanded.get(this.dataset.sectionId) === true);
      this.input.value = drafts.get(this.dataset.sectionId) || '';
      const options = { signal: this.abort.signal };
      this.details?.addEventListener('toggle', () => {
        const opened = this.details.open && !this.expanded;
        this.setExpanded(this.details.open);
        if (opened && document.activeElement === this.disclosure) this.focusInput();
      }, options);
      this.input.addEventListener('input', () => drafts.set(this.dataset.sectionId, this.input.value), options);
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-discount-toggle]') && this.details) {
          event.preventDefault();
          this.setExpanded(!this.details.open);
          if (this.details.open) this.focusInput();
          return;
        }
        if (event.target.closest('[data-cart-reload]')) { event.preventDefault(); window.location.reload(); return; }
        const button = event.target.closest('[data-discount-remove]');
        if (button) { event.preventDefault(); this.apply(button.dataset.discountRemove); }
        else if (event.target.closest('[data-discount-apply]')) { event.preventDefault(); this.apply(); }
      }, options);
      this.input.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') { event.preventDefault(); this.apply(); }
      }, options);
      this.render(lastCart || this.cart);
      this.setBusy(busy());
      const notice = notices.get(this.dataset.sectionId);
      if (notice) this.showNotice(notice);
      if (!hydration) {
        const started = version;
        hydration = json(`${root()}cart.js`).then((cart) => {
          if (started !== version) return;
          lastCart = cart;
          controls().forEach((element) => element.render(cart));
        }).catch(() => {});
      }
    }

    disconnectedCallback() { this.abort?.abort(); }

    setExpanded(value) {
      if (!this.details) return;
      this.expanded = Boolean(value);
      this.details.open = this.expanded;
      this.disclosure?.setAttribute('aria-expanded', String(this.expanded));
      expanded.set(this.dataset.sectionId, this.expanded);
    }

    focusInput() {
      requestAnimationFrame(() => {
        if (this.isConnected && this.details?.open && !this.input.disabled) this.input.focus({ preventScroll: true });
      });
    }

    setBusy(value) {
      this.input.disabled = value;
      this.applyButton.disabled = value;
      this.applyButton.setAttribute('aria-busy', String(mutation));
      this.querySelectorAll('[data-discount-remove]').forEach((button) => { button.disabled = value; });
    }

    showNotice(notice) {
      const status = this.querySelector('[data-discount-status]');
      status.textContent = this.strings[notice.key] || this.strings.error;
      status.hidden = false;
      status.dataset.state = notice.tone;
      this.input.setAttribute('aria-invalid', String(notice.key === 'invalid' || notice.key === 'empty'));
      if (notice.key === 'uncertain') {
        const refresh = document.createElement('button');
        refresh.type = 'button';
        refresh.dataset.cartReload = '';
        refresh.className = 'tuanty-cart-refresh';
        refresh.textContent = this.strings.reload;
        status.append(' ', refresh);
      }
    }

    render(cart) {
      this.cart = cart;
      const fragment = document.createDocumentFragment();
      for (const entry of codesFrom(cart)) {
        const chip = document.createElement('span');
        chip.className = 'tuanty-discount-chip';
        chip.dataset.state = entry.applicable ? 'applied' : 'unavailable';
        const label = document.createElement('span');
        label.textContent = entry.code;
        const state = document.createElement('span');
        state.className = entry.applicable ? 'sr-only' : 'tuanty-discount-state';
        state.textContent = entry.applicable ? this.strings.applied : this.strings.unavailable;
        const remove = document.createElement('button');
        remove.type = 'button';
        remove.dataset.discountRemove = entry.code;
        remove.textContent = '×';
        remove.setAttribute('aria-label', `${this.strings.remove_code} ${entry.code}`);
        chip.append(label, state, remove);
        fragment.append(chip);
      }
      this.querySelector('[data-discount-codes]').replaceChildren(fragment);
    }

    async apply(removeCode) {
      if (busy()) return this.showNotice({ key: uncertain ? 'uncertain' : 'busy', tone: 'error' });
      const code = this.input.value.trim();
      if (!removeCode && (!code || code.includes(','))) {
        this.setExpanded(true);
        this.focusInput();
        return this.showNotice({ key: !code ? 'empty' : 'invalid', tone: 'error' });
      }
      const sectionList = sectionIds();
      const section = this.dataset.sectionId;
      focusAfter = { discount: true, section, removeCode: Boolean(removeCode) };
      start();
      announce('applying', 'success', section);
      const verify = (cart) => removeCode ? !codesFrom(cart).some((entry) => normalize(entry.code) === normalize(removeCode)) : codesFrom(cart).some((entry) => normalize(entry.code) === normalize(code) && entry.applicable);
      try {
        const current = await json(`${root()}cart.js`);
        const previous = codesFrom(current).map((entry) => entry.code);
        const next = removeCode ? previous.filter((entry) => normalize(entry) !== normalize(removeCode)) : [...new Map([...previous, code].map((entry) => [normalize(entry), entry])).values()];
        let cart = await changeCart({ discount: next.join(',') }, sectionList);
        const accepted = verify(cart);
        if (!removeCode && !accepted && !previous.some((entry) => normalize(entry) === normalize(code))) {
          cart = await changeCart({ discount: previous.join(',') }, sectionList);
        }
        await renderCart(cart, sectionList);
        announce(accepted ? removeCode ? 'removed' : 'applied' : 'invalid', accepted ? 'success' : 'error', section);
        if (accepted) {
          drafts.delete(section);
          controls().filter((element) => element.dataset.sectionId === section).forEach((element) => { element.input.value = ''; });
        }
      } catch {
        await recover(sectionList, verify, removeCode ? 'removed' : 'applied', 'error');
      } finally { finish(); }
    }
  }

  if (!customElements.get('tuanty-cart-discount')) customElements.define('tuanty-cart-discount', TuantyCartDiscount);

  function closeSwipe(row) {
    row?.classList.remove('is-swipe-open', 'is-swiping');
    row?.style.removeProperty('--swipe-offset');
    const button = row?.querySelector('[data-swipe-remove]');
    if (button) { button.hidden = true; button.tabIndex = -1; button.setAttribute('aria-hidden', 'true'); }
  }

  function openSwipe(row) {
    document.querySelectorAll('[data-cart-swipe].is-swipe-open').forEach((other) => { if (other !== row) closeSwipe(other); });
    row.classList.remove('is-swiping');
    row.classList.add('is-swipe-open');
    row.style.setProperty('--swipe-offset', '-88px');
    const button = row.querySelector('[data-swipe-remove]');
    if (button) { button.hidden = false; button.tabIndex = 0; button.removeAttribute('aria-hidden'); }
  }

  document.addEventListener('click', (event) => {
    const row = rowFor(event.target);
    if (!row) {
      document.querySelectorAll('[data-cart-swipe].is-swipe-open').forEach(closeSwipe);
      return;
    }
    if (Date.now() < suppressClickUntil && !event.target.closest('[data-swipe-remove]')) {
      event.preventDefault(); event.stopImmediatePropagation(); return;
    }
    const remove = event.target.closest('[data-swipe-remove], .hdt-mini-cart__remove, .hdt-page-cart__remove');
    const button = event.target.closest('.hdt-quantity__button');
    if (!remove && !button) return;
    event.preventDefault(); event.stopImmediatePropagation();
    if (mutation || nativeBusy || uncertain) return;
    if (remove) return queueQuantity(row, 0, true);
    const input = quantityFor(row);
    if (!input) return;
    const before = input.value;
    button.name === 'plus' ? input.stepUp() : input.stepDown();
    if (input.value !== before && input.validity.valid) queueQuantity(row, Number(input.value), false, event.detail === 0 ? button.name : 'pointer');
  }, true);

  document.addEventListener('change', (event) => {
    const input = event.detail?.target || event.target;
    if (!input.matches?.('[name="updates[]"]')) return;
    const row = rowFor(input);
    if (!row) return;
    event.stopImmediatePropagation();
    if (!event.isTrusted) return;
    if (!input.value.trim() || !input.validity.valid || !Number.isInteger(Number(input.value))) {
      const confirmed = (lastCart || controls()[0]?.cart)?.items?.find((item) => item.key === row.dataset.cartKey);
      input.value = String(confirmed?.quantity ?? row.dataset.cartQuantity);
      pending.delete(row.dataset.cartKey);
      if (!pending.size) clearTimeout(timer);
      syncBusy();
      announce('invalid_quantity', 'error');
      return;
    }
    queueQuantity(row, Number(input.value));
  }, true);

  document.addEventListener('submit', (event) => {
    const productForm = event.target.matches('wrapp-hdt-pr-frm > form, wrapp-hdt-pr-frm-bundle > form');
    if (busy() && (event.target.matches('#CartPage-Form, #CartDrawer-Form, [data-tuanty-add]') || productForm)) {
      event.preventDefault(); event.stopImmediatePropagation(); announce(uncertain ? 'uncertain' : 'busy', 'error');
      return;
    }
    if (productForm) {
      nativeSubmitIntent = event.target;
      queueMicrotask(() => { if (nativeSubmitIntent === event.target) nativeSubmitIntent = null; });
    }
  }, true);

  document.addEventListener('pointerdown', (event) => {
    if (event.isPrimary === false) {
      if (gesture) closeSwipe(gesture.row);
      gesture = null;
      return;
    }
    const row = rowFor(event.target);
    if (event.pointerType !== 'touch' || !row || busy() || !matchMedia('(max-width: 767px)').matches || event.target.closest('button, input, [data-swipe-remove]')) return;
    gesture = { row, id: event.pointerId, x: event.clientX, y: event.clientY, dx: 0, horizontal: false, wasOpen: row.classList.contains('is-swipe-open') };
  }, { passive: true });

  document.addEventListener('pointermove', (event) => {
    if (!gesture || gesture.id !== event.pointerId) return;
    const dx = event.clientX - gesture.x;
    const dy = event.clientY - gesture.y;
    if (!gesture.horizontal) {
      if (Math.abs(dy) > 10 && Math.abs(dy) >= Math.abs(dx)) { gesture = null; return; }
      if (Math.abs(dx) < 12 || Math.abs(dx) <= Math.abs(dy) * 1.4) return;
      gesture.horizontal = true;
      gesture.row.classList.add('is-swiping');
      const remove = gesture.row.querySelector('[data-swipe-remove]');
      if (remove) remove.hidden = false;
    }
    if (event.cancelable) event.preventDefault();
    gesture.dx = dx;
    const offset = Math.max(-96, Math.min(0, (gesture.wasOpen ? -88 : 0) + dx));
    gesture.row.style.setProperty('--swipe-offset', `${offset}px`);
  }, { passive: false });

  document.addEventListener('pointerup', (event) => {
    if (!gesture || gesture.id !== event.pointerId) return;
    const active = gesture;
    gesture = null;
    if (!active.horizontal) return;
    suppressClickUntil = Date.now() + 350;
    if ((active.wasOpen && active.dx < 35) || (!active.wasOpen && active.dx < -48)) openSwipe(active.row);
    else closeSwipe(active.row);
  }, { passive: true });

  document.addEventListener('pointercancel', () => { if (gesture) closeSwipe(gesture.row); gesture = null; }, { passive: true });
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') document.querySelectorAll('[data-cart-swipe].is-swipe-open').forEach(closeSwipe);
  });
  document.addEventListener('cart:change', (event) => {
    if (!Array.isArray(event.detail?.cart?.items)) {
      uncertain = true;
      announce('uncertain', 'error');
      syncBusy();
      return;
    }
    version += 1;
    lastCart = event.detail.cart;
    nativeBusy = false;
    nativeForm = null;
    clearTimeout(nativeWatchdog);
    controls().forEach((element) => element.render(lastCart));
    syncBusy();
    if (pending.size && !mutation && !uncertain) timer = setTimeout(flushQuantity, 0);
  });
  document.addEventListener('theme:loading:doing', (event) => {
    if (event.detail?.tuantyControls || !nativeSubmitIntent) return;
    nativeForm = nativeSubmitIntent;
    nativeSubmitIntent = null;
    nativeBusy = true;
    clearTimeout(nativeWatchdog);
    nativeWatchdog = setTimeout(nativeTimeout, 20000);
    syncBusy();
  });
  document.addEventListener('cart:add:error', (event) => {
    if (!nativeBusy || event.target !== nativeForm) return;
    clearTimeout(nativeWatchdog);
    nativeBusy = false;
    nativeForm = null;
    syncBusy();
    if (pending.size) timer = setTimeout(flushQuantity, 0);
  });
  document.addEventListener('cart:drawer:render', restoreFocus);
  document.addEventListener('cart:page:render', restoreFocus);
})();
